<template>
  <div class="crowdfunding-page">
    <!-- メニューセクションより上（メニューセクションは含まない） -->
    <div class="top-section-wrapper" :style="{ backgroundColor: backgroundColor }">
      <div class="container">
        <!-- メインコンテンツエリア -->
        <div class="main-content">
          <!-- 左側：メディアプレースホルダー -->
          <div class="media-section">
            <div class="media-placeholder">
              <img
                v-if="vendor_image"
                :src="vendor_image"
                :alt="projectTitle || 'キャンペーン画像'"
                class="media-image"
              />
              <div v-else class="placeholder-content">
                <p>動画・画像</p>
              </div>
            </div>
          </div>

          <!-- 右側：プロフィールと情報 -->
          <div class="info-section">
            <!-- プロフィール画像と名前 -->
            <div class="profile-section">
              <img
                v-if="iconURL"
                :src="iconURL"
                :alt="vendor_ja || 'VTuber'"
                class="profile-image"
              />
              <div v-else class="profile-image-placeholder">
                <p>画像</p>
              </div>
              <h2 class="vendor-name">{{ vendor_ja || "配信者名" }}</h2>
            </div>

            <!-- タグ -->
            <div class="tags-section" v-if="tags && tags.length > 0">
              <span
                v-for="(tag, index) in tags"
                :key="index"
                class="tag"
                :style="{ borderColor: tagBorderColor }"
              >
                {{ tag }}
              </span>
            </div>

            <!-- キャンペーンタイトル -->
            <h1 class="project-title">{{ projectTitle || "プロジェクトタイトル" }}</h1>

            <!-- シェアボタン -->
            <div class="share-buttons">
              <button class="share-btn share-x" @click="shareOnX">
                <img src="/img/crowdfundingpic/x.webp" alt="X" class="share-icon-image" />
                <span>ポスト</span>
              </button>
              <button class="share-btn share-line" @click="shareOnLine">
                <img src="/img/crowdfundingpic/line.webp" alt="LINE" class="share-icon-image" />
                <span>LINEで送る</span>
              </button>
              <button class="share-btn share-url" @click="copyUrl">
                <img src="/img/crowdfundingpic/urlicon.webp" alt="URL" class="share-icon-image" />
                <span>URLをコピー</span>
              </button>
            </div>
          </div>
        </div>

        <!-- 下部：進捗とスケジュール -->
        <div class="bottom-section">
          <!-- 左側：進捗情報 -->
          <div class="progress-section">
            <h3 class="progress-title" :style="{ color: progressTitleColor }">現在の応援総額</h3>
            <div class="amount-display">
              {{ formatCurrency(currentAmount || 0) }}<span class="currency">円</span>
            </div>
            <div class="progress-bar-container">
              <div
                class="progress-bar"
                :style="{
                  width: Math.min(progressPercentage, 100) + '%',
                }"
              >
                <span class="progress-text">{{ progressPercentage }}%</span>
              </div>
            </div>
            <div class="metrics">
              <div class="metric-item">
                <div class="metric-icon">
                  <img
                    :src="
                      baseColor === 'blue'
                        ? '/img/crowdfundingpic/blue_coin.webp'
                        : '/img/crowdfundingpic/coin.webp'
                    "
                    alt="目標金額"
                    class="metric-icon-image"
                  />
                </div>
                <div class="metric-content">
                  <div class="metric-label" :style="{ color: metricLabelColor }">目標金額</div>
                  <div class="metric-value">{{ formatCurrency(targetAmount || 0) }}円</div>
                </div>
              </div>
              <div class="metric-item">
                <div class="metric-icon">
                  <img
                    :src="
                      baseColor === 'blue'
                        ? '/img/crowdfundingpic/blue_people.webp'
                        : '/img/crowdfundingpic/people.webp'
                    "
                    alt="応援者数"
                    class="metric-icon-image"
                  />
                </div>
                <div class="metric-content">
                  <div class="metric-label" :style="{ color: metricLabelColor }">応援者数</div>
                  <div class="metric-value">{{ supporterCount }}人</div>
                </div>
              </div>
              <div class="metric-item">
                <div class="metric-icon">
                  <img
                    :src="
                      baseColor === 'blue'
                        ? '/img/crowdfundingpic/blue_timer.webp'
                        : '/img/crowdfundingpic/timer.webp'
                    "
                    alt="残り日数"
                    class="metric-icon-image"
                  />
                </div>
                <div class="metric-content">
                  <div class="metric-label" :style="{ color: metricLabelColor }">残り日数</div>
                  <div class="metric-value">
                    {{ remainingDays === "開始前" || remainingDays === "終了" ? remainingDays : `${remainingDays}日` }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 右側：配信スケジュール -->
          <div class="schedule-section">
            <h3 class="schedule-title" :style="{ color: scheduleTitleColor }">
              <img
                :src="
                  baseColor === 'blue'
                    ? '/img/crowdfundingpic/blue_liveicon.webp'
                    : '/img/crowdfundingpic/liveicon.webp'
                "
                alt="配信スケジュール"
                class="schedule-icon"
              />
              配信スケジュール
            </h3>
            <div class="next-stream" v-if="nextStream || nowstreaming">
              <p class="next-stream-label">
                <span :style="{ color: scheduleTitleColor }">{{
                  nowstreaming ? "現在配信中" : "次回配信:"
                }}</span>
                {{ nextStream && !nowstreaming ? formatDate(nextStream.date) : "" }}
              </p>
              <button
                class="view-stream-btn"
                :class="{ 'view-stream-btn-disabled': !nowstreaming }"
                :style="{
                  background: nowstreaming ? viewStreamBtnBackground : '#cccccc',
                  borderColor: nowstreaming ? viewStreamBtnBorder : '#999999',
                }"
                @click="viewStreams"
                :disabled="!nowstreaming"
              >
                {{ nowstreaming ? "配信を見る" : "配信開始前" }}
                <span v-if="nowstreaming" class="arrow">▶</span>
              </button>
            </div>
            <table class="stream-list">
              <tbody>
                <tr v-for="(stream, index) in streamSchedule" :key="index" class="stream-item">
                  <td class="stream-label" :style="{ background: streamLabelBackground }">
                    {{ stream.label }}
                  </td>
                  <td class="stream-date">
                    <div class="stream-date-content">{{ formatDate(stream.date) }}</div>
                    <div
                      v-if="stream.liveId && isPastDate(stream.date)"
                      class="stream-archive-mobile"
                    >
                      <button
                        class="view-archive-btn"
                        :style="{
                          background: viewStreamBtnBackground,
                          borderColor: viewStreamBtnBorder,
                        }"
                        @click="viewArchive(stream.liveId)"
                      >
                        アーカイブを見る
                      </button>
                    </div>
                  </td>
                  <td class="stream-archive">
                    <button
                      v-if="stream.liveId && isPastDate(stream.date)"
                      class="view-archive-btn"
                      :style="{
                        background: viewStreamBtnBackground,
                        borderColor: viewStreamBtnBorder,
                      }"
                      @click="viewArchive(stream.liveId)"
                    >
                      アーカイブを見る
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- VTuberからのメッセージ -->
        <div class="message-section">
          <div class="message-content">
            <img
              v-if="iconURL"
              :src="iconURL"
              :alt="vendor_ja || 'VTuber'"
              class="message-profile-image"
            />
            <div v-else class="message-profile-image-placeholder">
              <p>画像</p>
            </div>
            <div
              class="message-bubble"
              :style="{
                background: messageBubbleBackground,
                '--bubble-arrow-color': messageBubbleBackground,
              }"
            >
              <p>{{ vtuberMessage || "メッセージがありません" }}</p>
            </div>
          </div>
        </div>

        <!-- 期間開始前メッセージセクション -->
        <div
          v-if="isBeforeEventStart"
          class="pre-event-message-section"
          :style="{ '--pre-event-border-color': supportHighlightColor }"
        >
          <p class="pre-event-message">
            本クラウドファンディングは、{{ formatDateWithSeconds(eventdate.start) }} 〜
            {{ formatDateWithSeconds(eventdate.end) }}
            となります。<br />事前にアカウントをご準備の上お待ちください。<br />
            <a href="/signup" class="pre-event-link" :style="{ color: supportHighlightColor }"
              >アカウント登録はこちら</a
            >
          </p>
        </div>

        <!-- 支援詳細セクション -->
        <div
          class="support-details-section"
          :style="{ '--support-border-color': supportHighlightColor }"
        >
          <h3 class="support-title" :style="{ color: supportTitleColor }">ご支援いただく皆様へ</h3>
          <p class="support-description">
            SukiSuki配信クラファンは、<span :style="{ color: supportHighlightColor }"
              >全てAll-or-Nothing</span
            >方式です。
          </p>
          <div class="support-conditions">
            <div class="condition-item">
              <span class="check-icon" :style="{ color: supportHighlightColor }">✓</span>
              <p>
                目標金額達成で<span :style="{ color: supportHighlightColor }">プロジェクト</span
                >が実行されます。
              </p>
            </div>
            <div class="condition-item">
              <span class="check-icon" :style="{ color: supportHighlightColor }">✓</span>
              <p>
                目標金額に達成しない場合は、<span :style="{ color: supportHighlightColor }"
                  >全額返金</span
                >されます。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="container">
    <!-- メニューバーセクション -->
    <div class="menu-section">
      <div class="menu-container">
        <h3 class="menu-title" :style="{ color: menuTitleColor }">Menu</h3>
        <nav class="menu-nav">
          <a href="#project-content" class="menu-item" :style="{ color: menuItemColor }">
            <span class="menu-text">プロジェクト内容</span> <span class="menu-arrow">▼</span>
          </a>
          <a href="#project-questions" class="menu-item" :style="{ color: menuItemColor }">
            <span class="menu-text">プロジェクトへの質問</span> <span class="menu-arrow">▼</span>
          </a>
          <a href="#featured-returns" class="menu-item" :style="{ color: menuItemColor }">
            <span class="menu-text">注目のリターン</span> <span class="menu-arrow">▼</span>
          </a>
          <a href="#activity-report" class="menu-item" :style="{ color: menuItemColor }">
            <span class="menu-text">活動報告</span> <span class="menu-arrow">▼</span>
          </a>
          <a href="#support-comments" class="menu-item" :style="{ color: menuItemColor }">
            <span class="menu-text">応援コメント</span> <span class="menu-arrow">▼</span>
          </a>
          <a href="#all-returns" class="menu-item" :style="{ color: menuItemColor }">
            <span class="menu-text">全てのリターン</span> <span class="menu-arrow">▼</span>
          </a>
        </nav>
      </div>
    </div>

    <!-- 2カラムセクション（プロジェクト内容とリターン） -->
    <div class="two-column-section">
      <!-- 左側：プロジェクト内容 -->
      <div class="project-content-column">
        <div class="column-header">
          <span class="column-label" :style="{ color: menuTitleColor }">Project</span>
          <h2
            class="column-title"
            :style="{
              color: baseColor === 'blue' ? '#3E23A9' : menuItemColor,
            }"
          >
            プロジェクト内容
            <span
              class="column-title-underline"
              :style="{
                backgroundColor: baseColor === 'blue' ? '#4378D3' : supportHighlightColor,
              }"
            ></span>
          </h2>
        </div>
        <div class="column-body">
          <!-- コンテンツブロックを動的に表示 -->
          <div class="column-body-content">
            <template v-for="block in contentBlocks" :key="block.id">
              <!-- 画像ブロック -->
              <div v-if="block.type === 'image' && block.isVisible" class="content-image-block">
                <img :src="block.url" :alt="block.alt || ''" />
              </div>
              <!-- 見出しブロック -->
              <h3
                v-if="block.type === 'heading' && block.level === 3 && block.isVisible"
                class="content-heading-h3"
              >
                <span class="heading-bullet" :style="{ color: supportHighlightColor }">●</span>
                <span v-html="sanitizeHtml(block.text)"></span>
              </h3>
              <h4
                v-if="block.type === 'heading' && block.level === 4 && block.isVisible"
                class="content-heading-h4"
                v-html="sanitizeHtml(block.text)"
              ></h4>
              <!-- 本文ブロック -->
              <div v-if="block.type === 'text' && block.isVisible" class="content-text-block">
                <p v-html="sanitizeHtml(block.text)"></p>
              </div>
            </template>
          </div>
          <!-- Q&Aセクション -->
          <div class="qa-section">
            <div class="qa-header">
              <span class="qa-label" :style="{ color: supportHighlightColor }">Q&A</span>
              <h2
                class="qa-title"
                :style="{
                  color: baseColor === 'blue' ? '#3E23A9' : menuItemColor,
                }"
              >
                プロジェクトへの質問
                <span
                  class="column-title-underline"
                  :style="{
                    backgroundColor: baseColor === 'blue' ? '#4378D3' : supportHighlightColor,
                  }"
                ></span>
              </h2>
            </div>
            <div class="qa-content">
              <div v-for="(qaItem, index) in displayedQAItems" :key="qaItem.id" class="qa-item">
                <div class="qa-question">
                  <span class="qa-q-label" :style="{ color: menuItemColor }">Q.</span>
                  <span class="qa-q-text">{{ qaItem.question }}</span>
                </div>
                <div class="qa-answer">
                  <span class="qa-a-label" :style="{ color: supportHighlightColor }">A.</span>
                  <span class="qa-a-text" v-html="sanitizeHtml(qaItem.answerHtml)"></span>
                </div>
                <div v-if="index < displayedQAItems.length - 1" class="qa-divider"></div>
              </div>
            </div>
            <div v-if="hasMoreQA" class="qa-footer">
              <a
                href="#"
                class="qa-view-more"
                :style="{ color: supportHighlightColor }"
                @click.prevent="showAllQA = !showAllQA"
              >
                {{ showAllQA ? "閉じる▲" : "もっと見る▼" }}
              </a>
            </div>
          </div>

          <!-- 注目のリターンセクション -->
          <div
            class="featured-returns-section"
            :style="{ backgroundColor: baseColor === 'blue' ? '#EDFAFF' : 'transparent' }"
          >
            <div class="featured-returns-header">
              <span class="featured-label" :style="{ color: supportHighlightColor }">Pickup</span>
              <h2
                class="featured-title"
                :style="{
                  color: baseColor === 'blue' ? '#3E23A9' : menuItemColor,
                }"
              >
                注目のリターン
                <span
                  class="column-title-underline"
                  :style="{
                    backgroundColor: baseColor === 'blue' ? '#4378D3' : supportHighlightColor,
                  }"
                ></span>
              </h2>
            </div>
            <div class="featured-returns-content">
              <div
                v-for="featuredItem in featuredReturns"
                :key="featuredItem.id"
                class="featured-return-card"
              >
                <div class="featured-return-main">
                  <div v-if="featuredItem.thumbUrl" class="featured-return-image">
                    <img :src="featuredItem.thumbUrl" :alt="featuredItem.planName" />
                  </div>
                  <div v-else class="featured-return-image-placeholder">
                    <p>画像</p>
                  </div>
                  <div class="featured-return-info">
                    <div class="featured-return-price">
                      {{ formatCurrency(featuredItem.price) }}円
                    </div>
                    <div class="featured-return-plan-name">{{ featuredItem.planName }}</div>
                    <div class="featured-return-details">
                      <span class="featured-detail-item" :style="{ color: supportHighlightColor }">
                        <img
                          v-if="baseColor === 'blue'"
                          src="/img/crowdfundingpic/blue_people.webp"
                          alt="応援者数"
                          class="detail-icon-image"
                        />
                        <span v-else>👥</span>
                        応援者数 {{ featuredItem.supporters }}人
                      </span>
                      <span class="featured-detail-item" :style="{ color: supportHighlightColor }">
                        <img
                          v-if="baseColor === 'blue'"
                          src="/img/crowdfundingpic/blue_bus.webp"
                          alt="お届け予定"
                          class="detail-icon-image"
                        />
                        <span v-else>🚚</span>
                        お届け予定 {{ featuredItem.deliveryDate }}
                      </span>
                    </div>
                    <button
                      class="featured-support-button"
                      :class="{ 'support-button-disabled': !isWithinEventPeriod }"
                      :style="{
                        background: isWithinEventPeriod ? supportBtnBackground : '#cccccc',
                        borderColor: isWithinEventPeriod ? supportBtnBorder : '#999999',
                      }"
                      @click="isWithinEventPeriod ? goToPayment(featuredItem) : null"
                      :disabled="!isWithinEventPeriod"
                    >
                      応援支援する <span class="arrow">▶</span>
                    </button>
                  </div>
                </div>
                <div v-if="featuredItem.description" class="featured-return-description">
                  <p
                    :class="{
                      'description-collapsed': !expandedDescriptions[`featured-${featuredItem.id}`],
                    }"
                    v-html="sanitizeHtml(featuredItem.description)"
                  ></p>
                </div>
                <a
                  v-if="featuredItem.description"
                  href="#"
                  class="featured-view-details"
                  :style="{ color: supportHighlightColor }"
                  @click.prevent="toggleDescription(`featured-${featuredItem.id}`)"
                >
                  {{
                    expandedDescriptions[`featured-${featuredItem.id}`]
                      ? "詳細を閉じる▲"
                      : "詳細を見る▼"
                  }}
                </a>
              </div>
            </div>
          </div>

          <!-- 活動報告セクション -->
          <div class="activity-report-section">
            <div class="activity-report-header">
              <span class="activity-label" :style="{ color: supportHighlightColor }"
                >Activity report</span
              >
              <h2
                class="activity-title"
                :style="{
                  color: baseColor === 'blue' ? '#3E23A9' : menuItemColor,
                }"
              >
                活動報告
                <span
                  class="column-title-underline"
                  :style="{
                    backgroundColor: baseColor === 'blue' ? '#4378D3' : supportHighlightColor,
                  }"
                ></span>
              </h2>
            </div>
            <div class="activity-report-content">
              <!-- 未ログインまたは未購入の場合 -->
              <div v-if="!isPurchaser" class="activity-report-locked">
                <p v-if="!currentUser" class="activity-report-locked-message">
                  活動報告を閲覧するにはログインが必要です。
                </p>
                <p v-else class="activity-report-locked-message">
                  活動報告はこのプロジェクトの支援応援者のみ閲覧できます。
                </p>
                <button
                  v-if="!currentUser"
                  class="activity-report-login-button"
                  :style="{ backgroundColor: supportHighlightColor }"
                  @click="router.push({ path: '/login', query: { redirect: route.fullPath } })"
                >
                  ログインする
                </button>
              </div>
              <!-- 購入者の場合 -->
              <template v-else>
                <div v-if="displayedActivityReports.length === 0" class="activity-report-empty">
                  <p>活動報告はありません</p>
                </div>
                <div
                  v-for="(report, index) in displayedActivityReports"
                  v-else
                  :key="report.id"
                  class="activity-report-item"
                >
                  <h3 class="activity-report-item-title">{{ report.title }}</h3>
                  <p class="activity-report-item-date">{{ formatDate(report.publishedAt) }}</p>
                  <div class="activity-report-item-body">
                    <template v-for="block in report.contentBlocks" :key="block.id">
                      <!-- 画像ブロック -->
                      <div
                        v-if="block.type === 'image' && block.isVisible"
                        class="activity-content-image-block"
                      >
                        <img :src="block.url" :alt="block.alt || ''" />
                      </div>
                      <!-- 見出しブロック -->
                      <h4
                        v-if="block.type === 'heading' && block.level === 3 && block.isVisible"
                        class="activity-content-heading-h3"
                      >
                        <span v-html="sanitizeHtml(block.text)"></span>
                      </h4>
                      <h5
                        v-if="block.type === 'heading' && block.level === 4 && block.isVisible"
                        class="activity-content-heading-h4"
                        v-html="sanitizeHtml(block.text)"
                      ></h5>
                      <!-- 本文ブロック -->
                      <div
                        v-if="block.type === 'text' && block.isVisible"
                        class="activity-content-text-block"
                      >
                        <p v-html="sanitizeHtml(block.text)"></p>
                      </div>
                    </template>
                  </div>
                  <div
                    v-if="index < displayedActivityReports.length - 1"
                    class="activity-report-divider"
                  ></div>
                </div>
              </template>
            </div>
            <div v-if="isPurchaser && hasMoreActivityReports" class="activity-report-footer">
              <a
                href="#"
                class="activity-report-view-more"
                :style="{ color: supportHighlightColor }"
                @click.prevent="showAllActivityReports = !showAllActivityReports"
              >
                {{ showAllActivityReports ? "閉じる▲" : "もっと見る▼" }}
              </a>
            </div>
          </div>

          <!-- 応援コメントセクション -->
          <div class="support-comments-section">
            <div class="support-comments-header">
              <span class="support-comments-label" :style="{ color: supportHighlightColor }"
                >Comments</span
              >
              <h2
                class="support-comments-title"
                :style="{
                  color: baseColor === 'blue' ? '#3E23A9' : menuItemColor,
                }"
              >
                応援コメント
                <span
                  class="column-title-underline"
                  :style="{
                    backgroundColor: baseColor === 'blue' ? '#4378D3' : supportHighlightColor,
                  }"
                ></span>
              </h2>
            </div>
            <div class="support-comments-content">
              <template v-if="displayedSupportComments.length === 0">
                <div class="support-comments-empty">
                  <p>応援コメントはありません</p>
                </div>
              </template>
              <template v-else>
                <div
                  v-for="comment in displayedSupportComments"
                  :key="comment.id"
                  class="support-comment-item"
                >
                  <div class="support-comment-avatar">
                    <img
                      v-if="comment.avatarUrl"
                      :src="comment.avatarUrl"
                      :alt="comment.nickname || 'ユーザー'"
                      class="comment-avatar-image"
                    />
                    <div v-else class="comment-avatar-placeholder"></div>
                  </div>
                  <div class="support-comment-content">
                    <div class="support-comment-nickname" :style="{ color: menuItemColor }">
                      {{ comment.nickname || "匿名" }}
                    </div>
                    <div class="support-comment-message">{{ comment.message }}</div>
                  </div>
                </div>
              </template>
            </div>
            <div v-if="hasMoreSupportComments" class="support-comments-footer">
              <button
                class="view-all-comments-btn"
                :style="{
                  background: viewStreamBtnBackground,
                  borderColor: viewStreamBtnBorder,
                }"
                @click="showAllSupportComments = !showAllSupportComments"
              >
                全てのコメントを見る <span class="arrow">▶</span>
              </button>
            </div>
          </div>

          <!-- 全てのリターンセクション（左カラム） -->
          <div
            class="all-returns-section-left"
            :style="{ backgroundColor: baseColor === 'blue' ? '#EDFAFF' : 'transparent' }"
          >
            <div class="column-header">
              <span class="column-label" :style="{ color: menuTitleColor }">All returns</span>
              <h2
                class="column-title"
                :style="{
                  color: baseColor === 'blue' ? '#3E23A9' : menuItemColor,
                }"
              >
                全てのリターン
                <span
                  class="column-title-underline"
                  :style="{
                    backgroundColor: baseColor === 'blue' ? '#4378D3' : supportHighlightColor,
                  }"
                ></span>
              </h2>
            </div>
            <div class="column-body">
              <!-- リターンカード -->
              <div v-for="returnItem in returnItems" :key="returnItem.id" class="return-card-left">
                <div class="return-card-content-left">
                  <div class="return-image-section-left">
                    <div v-if="returnItem.thumbUrl" class="return-image-left">
                      <img :src="returnItem.thumbUrl" :alt="returnItem.planName" />
                    </div>
                    <div v-else class="return-image-placeholder-left">
                      <p>画像</p>
                    </div>
                  </div>
                  <div class="return-info-section-left">
                    <div class="return-price-plan-left">
                      <div class="return-price-left">{{ formatCurrency(returnItem.price) }}円</div>
                      <div class="return-plan-name-left">{{ returnItem.planName }}</div>
                    </div>
                    <div class="return-details-left">
                      <div class="return-detail-item-left">
                        <span class="detail-icon">
                          <img
                            v-if="baseColor === 'blue'"
                            src="/img/crowdfundingpic/blue_people.webp"
                            alt="応援者数"
                            class="detail-icon-image"
                          />
                          <span v-else>👥</span>
                        </span>
                        <span>応援者数 {{ returnItem.supporters }}人</span>
                      </div>
                      <div class="return-detail-item-left">
                        <span class="detail-icon">
                          <img
                            v-if="baseColor === 'blue'"
                            src="/img/crowdfundingpic/blue_bus.webp"
                            alt="お届け予定"
                            class="detail-icon-image"
                          />
                          <span v-else>🚚</span>
                        </span>
                        <span>お届け予定 {{ returnItem.deliveryDate }}</span>
                      </div>
                    </div>
                    <div class="return-actions-left">
                      <a
                        href="#"
                        class="view-details-link-left"
                        :style="{ color: supportHighlightColor }"
                        @click.prevent="toggleDescription(`left-${returnItem.id}`)"
                      >
                        {{
                          expandedDescriptions[`left-${returnItem.id}`]
                            ? "詳細を閉じる▲"
                            : "詳細を見る▼"
                        }}
                      </a>
                      <button
                        class="support-button-left"
                        :class="{ 'support-button-disabled': !isWithinEventPeriod }"
                        :style="{
                          background: isWithinEventPeriod ? supportBtnBackground : '#cccccc',
                          borderColor: isWithinEventPeriod ? supportBtnBorder : '#999999',
                        }"
                        @click="isWithinEventPeriod ? goToPayment(returnItem) : null"
                        :disabled="!isWithinEventPeriod"
                      >
                        応援支援する <span class="arrow">▶</span>
                      </button>
                    </div>
                  </div>
                </div>
                <transition name="slide-down">
                  <div
                    v-if="returnItem.description && expandedDescriptions[`left-${returnItem.id}`]"
                    class="return-description-left"
                  >
                    <p v-html="sanitizeHtml(returnItem.description)"></p>
                  </div>
                </transition>
              </div>
            </div>
          </div>

          <!-- プロフィールセクション -->
          <div
            class="profile-section-left"
            :style="{ backgroundColor: baseColor === 'blue' ? '#C3F0FF' : 'transparent' }"
          >
            <div class="profile-card">
              <img
                v-if="iconURL"
                :src="iconURL"
                :alt="vendor_ja || 'VTuber'"
                class="profile-card-image"
              />
              <div v-else class="profile-card-image-placeholder">
                <p>画像</p>
              </div>
              <h3 class="profile-card-name">{{ vendor_ja || "配信者名" }}</h3>
              <div class="profile-links">
                <a
                  v-if="xUsername"
                  :href="`https://x.com/${xUsername}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="profile-link"
                >
                  <span class="profile-link-icon">
                    <img
                      v-if="baseColor === 'blue'"
                      src="/img/crowdfundingpic/blue_xicon.webp"
                      alt="X"
                      class="profile-link-icon-image"
                    />
                    <span v-else>X</span>
                  </span>
                  <span class="profile-link-text">@{{ xUsername }}</span>
                </a>
                <a
                  v-if="youtubeUsername"
                  :href="`https://www.youtube.com/@${youtubeUsername}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="profile-link"
                >
                  <span class="profile-link-icon">
                    <img
                      v-if="baseColor === 'blue'"
                      src="/img/crowdfundingpic/blue_youtubeicon.webp"
                      alt="YouTube"
                      class="profile-link-icon-image"
                    />
                    <span v-else>▶</span>
                  </span>
                  <span class="profile-link-text">@{{ youtubeUsername }}</span>
                </a>
                <a
                  v-if="homeURL"
                  :href="homeURL"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="profile-link"
                >
                  <span class="profile-link-icon">
                    <img
                      v-if="baseColor === 'blue'"
                      src="/img/crowdfundingpic/blue_homeicon.webp"
                      alt="Link"
                      class="profile-link-icon-image"
                    />
                    <span v-else>🔗</span>
                  </span>
                  <span class="profile-link-text">{{ homeURL }}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右側：全てのリターン -->
      <div
        class="returns-column"
        :style="{
          backgroundColor: baseColor === 'blue' ? '#EDFAFF' : 'transparent',
        }"
        @mouseenter="isHoveringRightColumn = true"
        @mouseleave="isHoveringRightColumn = false"
      >
        <div class="column-header">
          <span class="column-label" :style="{ color: menuTitleColor }">All returns</span>
          <h2
            class="column-title"
            :style="{
              color: baseColor === 'blue' ? '#3E23A9' : menuItemColor,
            }"
          >
            全てのリターン
            <span
              class="column-title-underline"
              :style="{
                backgroundColor: baseColor === 'blue' ? '#4378D3' : supportHighlightColor,
              }"
            ></span>
          </h2>
        </div>
        <div class="column-body">
          <!-- リターンカード -->
          <div v-for="returnItem in returnItems" :key="returnItem.id" class="return-card">
            <div class="return-card-content">
              <div class="return-main-section">
                <div v-if="returnItem.thumbUrl" class="return-image">
                  <img :src="returnItem.thumbUrl" :alt="returnItem.planName" />
                </div>
                <div v-else class="return-image-placeholder">
                  <p>画像</p>
                </div>
                <div class="return-price-plan">
                  <div class="return-price">{{ formatCurrency(returnItem.price) }}円</div>
                  <div class="return-plan-name">{{ returnItem.planName }}</div>
                </div>
              </div>
              <div class="return-details-section">
                <div class="return-details">
                  <div class="return-detail-item">
                    <span class="detail-icon">
                      <img
                        v-if="baseColor === 'blue'"
                        src="/img/crowdfundingpic/blue_people.webp"
                        alt="応援者数"
                        class="detail-icon-image"
                      />
                      <span v-else>👥</span>
                    </span>
                    <span>応援者数 {{ returnItem.supporters }}人</span>
                  </div>
                  <div class="return-detail-item">
                    <span class="detail-icon">
                      <img
                        v-if="baseColor === 'blue'"
                        src="/img/crowdfundingpic/blue_bus.webp"
                        alt="お届け予定"
                        class="detail-icon-image"
                      />
                      <span v-else>🚚</span>
                    </span>
                    <span>お届け予定 {{ returnItem.deliveryDate }}</span>
                  </div>
                </div>
                <div class="return-actions">
                  <a
                    href="#"
                    class="view-details-link"
                    :style="{ color: supportHighlightColor }"
                    @click.prevent="toggleDescription(`right-${returnItem.id}`)"
                  >
                    {{
                      expandedDescriptions[`right-${returnItem.id}`]
                        ? "詳細を閉じる▲"
                        : "詳細を見る▼"
                    }}
                  </a>
                  <button
                    class="support-button"
                    :class="{ 'support-button-disabled': !isWithinEventPeriod }"
                    :style="{
                      background: isWithinEventPeriod ? supportBtnBackground : '#cccccc',
                      borderColor: isWithinEventPeriod ? supportBtnBorder : '#999999',
                    }"
                    @click="isWithinEventPeriod ? goToPayment(returnItem) : null"
                    :disabled="!isWithinEventPeriod"
                  >
                    応援支援する <span class="arrow">▶</span>
                  </button>
                </div>
              </div>
              <transition name="slide-down">
                <div
                  v-if="returnItem.description && expandedDescriptions[`right-${returnItem.id}`]"
                  class="return-description"
                >
                  <p v-html="sanitizeHtml(returnItem.description)"></p>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  getDoc,
  doc,
  collection,
  getDocs,
  query,
  orderBy,
  where,
  onSnapshot,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import DOMPurify from "dompurify";
import db from "../main.js";
import { useMeta } from "@/useMeta.js";

const route = useRoute();
const router = useRouter();
const campaignId = ref(route.params.campaignId);

// メタ情報の設定
const { updateMeta } = useMeta();

// Firestoreから取得するデータ
const baseColor = ref(null);
const currentAmount = ref(0);
const nowstreaming = ref(false);
const projectTitle = ref("");
const tags = ref([]);
const targetAmount = ref(0);
const vendor_image = ref(null);
const iconURL = ref(null);
const vendor_ja = ref("");
const xUsername = ref("");
const youtubeUsername = ref("");
const homeURL = ref("");
const eventdate = ref(null);
const livedate = ref(null);

// その他のデータ（仮データまたは将来的にFirestoreから取得）
const supporterCount = ref(10); // 仮データ
const nextStream = ref(null);
const streamSchedule = ref([]);
const vtuberMessage = ref("");

// リターンアイテム
const returnItems = ref([]);
// 注目のリターン（ピックアップ）
const featuredReturns = ref([]);

// プロジェクト内容ブロック
const contentBlocks = ref([]);

// Q&Aデータ
const qaItems = ref([]);
const showAllQA = ref(false);

// 認証・購入者チェック
const currentUser = ref(null);
const isPurchaser = ref(false);

// 活動報告データ
const activityReports = ref([]);
const showAllActivityReports = ref(false);

// 応援コメントデータ
const supportComments = ref([]);
const showAllSupportComments = ref(false);

// 注目のリターンの説明文の展開状態
const expandedDescriptions = ref({});

// 説明文の展開/折りたたみを切り替え
const toggleDescription = (returnId) => {
  expandedDescriptions.value[returnId] = !expandedDescriptions.value[returnId];
};

// 右カラムのスクロール制御
const isHoveringRightColumn = ref(false);

// HTMLをサニタイズする関数
const sanitizeHtml = (html) => {
  if (!html) return "";
  // 基本的なHTMLタグを許可（br, p, strong, em, ul, ol, li, aなど）
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["br", "p", "strong", "em", "u", "b", "i", "ul", "ol", "li", "a", "span", "div"],
    ALLOWED_ATTR: ["href", "target", "rel", "style"],
  });
};

const toOrderNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const sortByOrder = (items) => {
  return items.slice().sort((a, b) => toOrderNumber(a.order) - toOrderNumber(b.order));
};

// ウィンドウ幅の管理
const windowWidth = ref(window.innerWidth);

const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth;
};

// ローディング状態
const isLoading = ref(true);

// リアルタイムリスナーのクリーンアップ用
const unsubscribeFunctions = ref([]);

// 背景色を計算
const backgroundColor = computed(() => {
  if (baseColor.value === "blue") {
    return "#EDFAFF";
  }
  return baseColor.value || "#f5f0ff";
});

// タグの枠線色を計算
const tagBorderColor = computed(() => {
  if (baseColor.value === "blue") {
    return "#4378D3";
  }
  return "#ffb3d9";
});

// 進捗タイトルの文字色を計算
const progressTitleColor = computed(() => {
  if (baseColor.value === "blue") {
    return "#3E23A9";
  }
  return "#333";
});

// メトリクスラベルの文字色を計算
const metricLabelColor = computed(() => {
  if (baseColor.value === "blue") {
    return "#3E23A9";
  }
  return "#999";
});

// 配信スケジュールタイトルの文字色を計算
const scheduleTitleColor = computed(() => {
  if (baseColor.value === "blue") {
    return "#3E23A9";
  }
  return "#333";
});

// 配信を見るボタンの背景色を計算
const viewStreamBtnBackground = computed(() => {
  if (baseColor.value === "blue") {
    return "#4378D3";
  }
  return "#ff6b9d";
});

// 配信を見るボタンの枠線色を計算
const viewStreamBtnBorder = computed(() => {
  if (baseColor.value === "blue") {
    return "#3E23A9";
  }
  return "#ff6b9d";
});

// 応援支援するボタンの背景色を計算
const supportBtnBackground = computed(() => {
  if (baseColor.value === "blue") {
    return "linear-gradient(90deg, #C3F0FF 0%, #4378D3 50%, #3E23A9 100%)";
  }
  return "#ff6b9d";
});

// 応援支援するボタンの枠線色を計算
const supportBtnBorder = computed(() => {
  if (baseColor.value === "blue") {
    return "#3E23A9";
  }
  return "#ff6b9d";
});

// 配信スケジュールラベルの背景色を計算
const streamLabelBackground = computed(() => {
  if (baseColor.value === "blue") {
    return "#4378D3";
  }
  return "#ff6b9d";
});

// メッセージバブルの背景色を計算
const messageBubbleBackground = computed(() => {
  if (baseColor.value === "blue") {
    return "#C3F0FF";
  }
  return "#ffb3d9";
});

// 支援詳細セクションのタイトル色を計算
const supportTitleColor = computed(() => {
  if (baseColor.value === "blue") {
    return "#3E23A9";
  }
  return "#8e24aa";
});

// 支援詳細セクションのハイライト色を計算
const supportHighlightColor = computed(() => {
  if (baseColor.value === "blue") {
    return "#4378D3";
  }
  return "#ff6b9d";
});

// メニュータイトルの色を計算
const menuTitleColor = computed(() => {
  if (baseColor.value === "blue") {
    return "#4378D3";
  }
  return "#999";
});

// メニュー項目の色を計算
const menuItemColor = computed(() => {
  if (baseColor.value === "blue") {
    return "#3E23A9";
  }
  return "#8e24aa";
});

// 進捗率を計算
const progressPercentage = computed(() => {
  if (!targetAmount.value || targetAmount.value === 0) return 0;
  const percentage = (currentAmount.value / targetAmount.value) * 100;
  return Math.round(percentage);
});

// 残り日数を計算
const remainingDays = computed(() => {
  if (!eventdate.value || !eventdate.value.start || !eventdate.value.end) {
    return "0";
  }

  const now = new Date();
  const startDate = convertTimestampToDate(eventdate.value.start);
  const endDate = convertTimestampToDate(eventdate.value.end);

  if (!startDate || !endDate) {
    return "0";
  }

  // 開始前の場合
  if (now < startDate) {
    return "開始前";
  }

  // 終了日から現在日時を引いて残り日数を計算
  const diffTime = endDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // 終了日時を過ぎている場合は「終了」（diffTime で判定。diffDays は終了直後に 0 になることがある）
  if (diffTime < 0) return "終了";
  // 最終日は1日表記
  if (diffDays === 0) return "1";
  return diffDays.toString();
});

// 期間内かどうかを判定
const isWithinEventPeriod = computed(() => {
  if (!eventdate.value || !eventdate.value.start || !eventdate.value.end) {
    return false;
  }

  const now = new Date();
  const startDate = convertTimestampToDate(eventdate.value.start);
  const endDate = convertTimestampToDate(eventdate.value.end);

  if (!startDate || !endDate) {
    return false;
  }

  // 開始日時 <= 現在時刻 <= 終了日時 の場合は期間内
  return now >= startDate && now <= endDate;
});

// Firestoreからデータを取得（初期ロード用、リアルタイム更新不要なデータ）
const fetchCampaignData = async () => {
  try {
    isLoading.value = true;
    const docRef = doc(db, "Crowdfunding_projects", campaignId.value);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      baseColor.value = data.baseColor || "#f5f0ff";
      projectTitle.value = data.projectTitle || "";
      tags.value = data.tag || [];
      targetAmount.value = data.targetAmount || 0;
      vendor_image.value = data.vendor_image || null;
      iconURL.value = data.iconURL || null;
      vendor_ja.value = data.vendor_ja || "";
      xUsername.value = data.xUsername || "";
      youtubeUsername.value = data.youtubeUsername || "";
      homeURL.value = data.homeURL || "";
      eventdate.value = data.eventdate || null;
      livedate.value = data.livedate || null;
      vtuberMessage.value = data.vendorMessage || "";

      // 初期値として設定（リアルタイム更新で上書きされる）
      currentAmount.value = data.currentAmount || 0;
      nowstreaming.value = data.nowstreaming || false;
      supporterCount.value = data.supporterCount || 0;

      // メタデータの更新
      const currentUrl =
        typeof window !== "undefined"
          ? window.location.href
          : `https://sukisuki.live/crowdfunding/${campaignId.value}`;

      // 画像URLを絶対URLに変換（相対URLの場合は絶対URLに変換）
      const getAbsoluteImageUrl = (imageUrl) => {
        if (!imageUrl || imageUrl.trim() === "") return "";

        // 既に絶対URLの場合はそのまま返す
        if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
          return imageUrl;
        }

        // 相対URLの場合は絶対URLに変換
        const baseUrl = "https://sukisuki.live";
        if (imageUrl.startsWith("/")) {
          return `${baseUrl}${imageUrl}`;
        }
        return `${baseUrl}/${imageUrl}`;
      };

      // vendor_imageを優先し、なければiconURLを使用
      const imageUrl = data.vendor_image || data.iconURL;
      const ogImageUrl = getAbsoluteImageUrl(imageUrl);

      // デバッグ用（本番環境では削除可能）
      if (process.env.NODE_ENV === "development") {
        console.log("OGP Image URL:", ogImageUrl);
        console.log("vendor_image:", data.vendor_image);
        console.log("iconURL:", data.iconURL);
      }

      // メタ情報を更新（プレレンダリング時に確実に実行されるように）
      if (ogImageUrl) {
        updateMeta({
          title: `${data.projectTitle || "クラウドファンディング"} | SukiSukiらいぶすてーしょん`,
          description: `${
            data.projectTitle || "クラウドファンディング"
          }で応援支援しよう！SukiSuki配信クラファン`,
          ogTitle: `${data.projectTitle || "クラウドファンディング"} | SukiSukiらいぶすてーしょん`,
          ogDescription: `${
            data.projectTitle || "クラウドファンディング"
          }で応援支援しよう！SukiSuki配信クラファン`,
          ogImage: ogImageUrl,
          ogType: "website",
          ogUrl: currentUrl,
          ogSiteName: "SukiSukiらいぶすてーしょん",
          twitterCard: "summary_large_image",
          twitterTitle: `${
            data.projectTitle || "クラウドファンディング"
          } | SukiSukiらいぶすてーしょん`,
          twitterDescription: `${
            data.projectTitle || "クラウドファンディング"
          }で応援支援しよう！SukiSuki配信クラファン`,
          twitterImage: ogImageUrl,
        });
      } else {
        // 画像URLがない場合でも、他のメタ情報は設定
        updateMeta({
          title: `${data.projectTitle || "クラウドファンディング"} | SukiSukiらいぶすてーしょん`,
          description: `${
            data.projectTitle || "クラウドファンディング"
          }で応援支援しよう！SukiSuki配信クラファン`,
          ogTitle: `${data.projectTitle || "クラウドファンディング"} | SukiSukiらいぶすてーしょん`,
          ogDescription: `${
            data.projectTitle || "クラウドファンディング"
          }で応援支援しよう！SukiSuki配信クラファン`,
          ogImage: "",
          ogType: "website",
          ogUrl: currentUrl,
          ogSiteName: "SukiSukiらいぶすてーしょん",
          twitterCard: "summary_large_image",
          twitterTitle: `${
            data.projectTitle || "クラウドファンディング"
          } | SukiSukiらいぶすてーしょん`,
          twitterDescription: `${
            data.projectTitle || "クラウドファンディング"
          }で応援支援しよう！SukiSuki配信クラファン`,
          twitterImage: "",
        });
      }

      // livedateからstreamScheduleを生成
      streamSchedule.value = [];
      if (livedate.value) {
        if (livedate.value.first) {
          streamSchedule.value.push({
            label: "初回配信",
            date: livedate.value.first,
            liveId: null, // 後で取得
          });
        }
        if (livedate.value.second) {
          streamSchedule.value.push({
            label: "2回目配信",
            date: livedate.value.second,
            liveId: null, // 後で取得
          });
        }
        if (livedate.value.third) {
          streamSchedule.value.push({
            label: "3回目配信",
            date: livedate.value.third,
            liveId: null, // 後で取得
          });
        }
        if (livedate.value.end) {
          streamSchedule.value.push({
            label: "最終配信",
            date: livedate.value.end,
            liveId: null, // 後で取得
          });
        }
      }

      // 各配信に対応するlive_idを取得
      await fetchLiveIdsForSchedule();

      // 次回配信を設定（現在の日付より未来で最も近い配信日）
      nextStream.value = null;
      const now = new Date();
      const futureStreams = streamSchedule.value
        .map((stream) => {
          const streamDate = convertTimestampToDate(stream.date);
          return streamDate ? { ...stream, dateObj: streamDate } : null;
        })
        .filter((stream) => stream && stream.dateObj > now)
        .sort((a, b) => a.dateObj - b.dateObj);

      if (futureStreams.length > 0) {
        nextStream.value = {
          date: futureStreams[0].date,
        };
      }
    } else {
      console.error("キャンペーンデータが見つかりません");
    }
  } catch (error) {
    console.error("データ取得エラー:", error);
  } finally {
    isLoading.value = false;
  }
};

// 各配信に対応するlive_idを取得
const fetchLiveIdsForSchedule = async () => {
  try {
    // Crowdfunding_livesheduleからprojectidでクエリ
    const livesQuery = query(
      collection(db, "Crowdfunding_liveshedule"),
      where("projectid", "==", campaignId.value)
    );

    const livesSnapshot = await getDocs(livesQuery);

    if (livesSnapshot.empty) {
      return;
    }

    // ライブスケジュールをマップに変換（livedate.startをキーとして）
    const liveMap = new Map();
    livesSnapshot.docs.forEach((doc) => {
      const data = doc.data();
      if (data.livedate?.start) {
        const startTime = data.livedate.start.toMillis?.() || 0;
        liveMap.set(startTime, doc.id);
      }
    });

    // streamScheduleの各配信に対応するlive_idを設定
    streamSchedule.value.forEach((stream) => {
      const streamDate = convertTimestampToDate(stream.date);
      if (streamDate) {
        const streamTime = streamDate.getTime();
        // タイムスタンプが完全に一致しない場合もあるため、±30分の範囲で検索
        for (const [liveTime, liveId] of liveMap.entries()) {
          const diff = Math.abs(liveTime - streamTime);
          // 30分以内の差であれば一致とみなす
          if (diff < 1800000) {
            stream.liveId = liveId;
            break;
          }
        }
      }
    });
  } catch (error) {
    console.error("ライブID取得エラー:", error);
  }
};

// アーカイブページに遷移
const viewArchive = (liveId) => {
  if (liveId) {
    router.push({
      path: `/crowdfunding/${campaignId.value}/archive`,
      query: { live_id: liveId },
    });
  }
};

// リアルタイム更新：currentAmount, supporterCount, nowstreaming
const setupCampaignRealtimeListener = () => {
  const docRef = doc(db, "Crowdfunding_projects", campaignId.value);

  const unsubscribe = onSnapshot(
    docRef,
    (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        currentAmount.value = data.currentAmount || 0;
        nowstreaming.value = data.nowstreaming || false;
        supporterCount.value = data.supporterCount || 0;

        // リアルタイム更新時にもメタ情報を更新（画像URLが変更された場合に備えて）
        if (data.vendor_image || data.iconURL) {
          const imageUrl = data.vendor_image || data.iconURL;
          const getAbsoluteImageUrl = (imageUrl) => {
            if (!imageUrl || imageUrl.trim() === "") return "";
            if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
              return imageUrl;
            }
            const baseUrl = "https://sukisuki.live";
            if (imageUrl.startsWith("/")) {
              return `${baseUrl}${imageUrl}`;
            }
            return `${baseUrl}/${imageUrl}`;
          };

          const ogImageUrl = getAbsoluteImageUrl(imageUrl);
          const currentUrl =
            typeof window !== "undefined"
              ? window.location.href
              : `https://sukisuki.live/crowdfunding/${campaignId.value}`;

          if (ogImageUrl) {
            updateMeta({
              title: `${
                data.projectTitle || projectTitle.value || "クラウドファンディング"
              } | SukiSukiらいぶすてーしょん`,
              description: `${
                data.projectTitle || projectTitle.value || "クラウドファンディング"
              }で応援支援しよう！SukiSuki配信クラファン`,
              ogTitle: `${
                data.projectTitle || projectTitle.value || "クラウドファンディング"
              } | SukiSukiらいぶすてーしょん`,
              ogDescription: `${
                data.projectTitle || projectTitle.value || "クラウドファンディング"
              }で応援支援しよう！SukiSuki配信クラファン`,
              ogImage: ogImageUrl,
              ogType: "website",
              ogUrl: currentUrl,
              ogSiteName: "SukiSukiらいぶすてーしょん",
              twitterCard: "summary_large_image",
              twitterTitle: `${
                data.projectTitle || projectTitle.value || "クラウドファンディング"
              } | SukiSukiらいぶすてーしょん`,
              twitterDescription: `${
                data.projectTitle || projectTitle.value || "クラウドファンディング"
              }で応援支援しよう！SukiSuki配信クラファン`,
              twitterImage: ogImageUrl,
            });
          }
        }
      }
    },
    (error) => {
      console.error("リアルタイム更新エラー（キャンペーンデータ）:", error);
    }
  );

  unsubscribeFunctions.value.push(unsubscribe);
};

// コンテンツブロックを取得
const fetchContentBlocks = async () => {
  try {
    const contentCollection = collection(db, "Crowdfunding_projects", campaignId.value, "content");
    const q = query(contentCollection, where("isVisible", "==", true), orderBy("order", "asc"));
    const querySnapshot = await getDocs(q);

    const blocks = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    contentBlocks.value = sortByOrder(blocks);
  } catch (error) {
    console.error("コンテンツブロック取得エラー:", error);
  }
};

// Q&Aデータを取得
const fetchQAItems = async () => {
  try {
    const qaCollection = collection(db, "Crowdfunding_projects", campaignId.value, "qa");
    const q = query(qaCollection, where("isVisible", "==", true), orderBy("order", "asc"));
    const querySnapshot = await getDocs(q);

    const items = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    qaItems.value = sortByOrder(items);
  } catch (error) {
    console.error("Q&Aデータ取得エラー:", error);
  }
};

// リターンアイテムを取得
const fetchReturnItems = async () => {
  try {
    const returnsCollection = collection(db, "Crowdfunding_projects", campaignId.value, "returns");
    const q = query(returnsCollection, where("isVisible", "==", true), orderBy("order", "asc"));
    const querySnapshot = await getDocs(q);

    const allReturns = querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        order: data.order ?? 0,
        price: data.price || 0,
        planName: data.title || "",
        supporters: data.supporterCount || 0,
        deliveryDate: data.deliveryAt || "",
        thumbUrl: data.thumbUrl || null,
        isSoldOut: data.isSoldOut || false,
        description: data.description || "",
        isFeatured: data.isFeatured || false,
      };
    });

    // 全てのリターン
    const sortedReturns = sortByOrder(allReturns);
    returnItems.value = sortedReturns;
    // 注目のリターン（isFeaturedがtrueのもの、または最初の3件）
    featuredReturns.value = sortedReturns.filter((item) => item.isFeatured).slice(0, 3);
    if (featuredReturns.value.length === 0) {
      featuredReturns.value = sortedReturns.slice(0, 3);
    }
  } catch (error) {
    console.error("リターンアイテム取得エラー:", error);
  }
};

// 活動報告を取得
const fetchActivityReports = async () => {
  try {
    const reportsCollection = collection(
      db,
      "Crowdfunding_projects",
      campaignId.value,
      "activityReports"
    );
    const q = query(reportsCollection, where("isVisible", "==", true), orderBy("order", "asc"));
    const querySnapshot = await getDocs(q);

    const reports = await Promise.all(
      querySnapshot.docs.map(async (doc) => {
        const data = doc.data();
        // 各活動報告のコンテンツブロックを取得
        const contentBlocks = await fetchActivityReportContent(doc.id);
        return {
          id: doc.id,
          order: data.order ?? 0,
          title: data.title || "",
          publishedAt: data.publishedAt ? data.publishedAt.toDate().toISOString() : "",
          summary: data.summary || "",
          coverUrl: data.coverUrl || null,
          contentBlocks: contentBlocks,
        };
      })
    );

    activityReports.value = sortByOrder(reports);
  } catch (error) {
    console.error("活動報告取得エラー:", error);
  }
};

// 活動報告のコンテンツブロックを取得
const fetchActivityReportContent = async (reportId) => {
  try {
    const contentCollection = collection(
      db,
      "Crowdfunding_projects",
      campaignId.value,
      "activityReports",
      reportId,
      "content"
    );
    const q = query(contentCollection, where("isVisible", "==", true), orderBy("order", "asc"));
    const querySnapshot = await getDocs(q);

    const blocks = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    return sortByOrder(blocks);
  } catch (error) {
    console.error("活動報告コンテンツ取得エラー:", error);
    return [];
  }
};

// 応援コメントを取得（リアルタイム更新）
const setupSupportCommentsRealtimeListener = () => {
  const commentsCollection = collection(
    db,
    "Crowdfunding_projects",
    campaignId.value,
    "supportComments"
  );
  // isVisibleでフィルタリングし、createdAtで降順ソート
  const q = query(commentsCollection, where("isVisible", "==", true), orderBy("createdAt", "desc"));

  const unsubscribe = onSnapshot(
    q,
    (querySnapshot) => {
      const comments = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          nickname: data.nickname || "匿名",
          message: data.message || "",
          avatarUrl: data.avatarUrl || null,
          supporterUid: data.supporterUid || null,
          orderId: data.orderId || null,
          isPinned: data.isPinned || false,
          createdAt: data.createdAt ? data.createdAt.toDate().toISOString() : "",
        };
      });

      // クライアント側でソート: isPinnedがtrueのものを先に、その後createdAtで降順ソート
      supportComments.value = comments.sort((a, b) => {
        if (a.isPinned !== b.isPinned) {
          return b.isPinned ? 1 : -1; // isPinnedがtrueのものを先に
        }
        // isPinnedが同じ場合、createdAtで降順ソート
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
    },
    (error) => {
      console.error("応援コメント取得エラー:", error);
    }
  );

  unsubscribeFunctions.value.push(unsubscribe);
};

// 表示するQ&Aアイテムを計算
const displayedQAItems = computed(() => {
  if (showAllQA.value) {
    return qaItems.value;
  }
  return qaItems.value.slice(0, 2);
});

// もっと見るボタンを表示するかどうか
const hasMoreQA = computed(() => {
  return qaItems.value.length > 2;
});

// 表示する活動報告を計算
const displayedActivityReports = computed(() => {
  if (showAllActivityReports.value) {
    return activityReports.value;
  }
  return activityReports.value.slice(0, 1);
});

// もっと見るボタンを表示するかどうか（活動報告）
const hasMoreActivityReports = computed(() => {
  return activityReports.value.length > 1;
});

// 表示する応援コメントを計算
const displayedSupportComments = computed(() => {
  if (showAllSupportComments.value) {
    return supportComments.value;
  }
  return supportComments.value.slice(0, 3);
});

// もっと見るボタンを表示するかどうか（応援コメント）
const hasMoreSupportComments = computed(() => {
  return supportComments.value.length > 3;
});

// 通貨フォーマット
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("ja-JP").format(amount);
};

// FirestoreのタイムスタンプをDateオブジェクトに変換
const convertTimestampToDate = (timestamp) => {
  if (!timestamp) return null;
  // FirestoreのTimestampオブジェクトの場合
  if (timestamp && typeof timestamp.toDate === "function") {
    return timestamp.toDate();
  }
  // 文字列の場合
  if (typeof timestamp === "string") {
    return new Date(timestamp);
  }
  // Dateオブジェクトの場合
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return null;
};

// 日付フォーマット
const formatDate = (dateInput) => {
  if (!dateInput) return "";
  const date = convertTimestampToDate(dateInput);
  if (!date) return "";
  const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const weekday = weekdays[date.getDay()];
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}年${month}月${day}日(${weekday})${hours}:${minutes}`;
};

// 日付フォーマット（秒まで含む）
const formatDateWithSeconds = (dateInput) => {
  if (!dateInput) return "";
  const date = convertTimestampToDate(dateInput);
  if (!date) return "";
  const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const weekday = weekdays[date.getDay()];
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}年${month}月${day}日(${weekday}) ${hours}時${minutes}分${seconds}秒`;
};

// 日時が過去かどうかを判定
const isPastDate = (dateInput) => {
  if (!dateInput) return false;
  const date = convertTimestampToDate(dateInput);
  if (!date) return false;
  const now = new Date();
  return date < now;
};

// 期間開始前かどうかを判定
const isBeforeEventStart = computed(() => {
  if (!eventdate.value || !eventdate.value.start) {
    return false;
  }

  const now = new Date();
  const startDate = convertTimestampToDate(eventdate.value.start);

  if (!startDate) {
    return false;
  }

  return now < startDate;
});

// シェア機能
const shareOnX = () => {
  const url = window.location.href;
  // プロジェクトタイトルを含むテキストを作成
  let text = "";
  if (projectTitle.value) {
    text = `${projectTitle.value}\n\n`;
  }

  // ハッシュタグを追加
  const hashtags = ["クラウドファンディング", "VTuber", "SukiSuki", "配信クラファン"];
  if (hashtags.length > 0) {
    text += hashtags.map((tag) => `#${tag}`).join(" ") + "\n\n";
  }

  // URLを追加
  text += url;

  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, "_blank");
};

const shareOnLine = () => {
  const url = window.location.href;
  // プロジェクトタイトルを含むテキストを作成
  let text = "";
  if (projectTitle.value) {
    text = projectTitle.value;
  }

  // LINEのシェアURLを作成（textとurlの両方を指定）
  const shareUrl = `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(url)}${
    text ? `&text=${encodeURIComponent(text)}` : ""
  }`;

  window.open(shareUrl, "_blank");
};

const copyUrl = async () => {
  try {
    await navigator.clipboard.writeText(window.location.href);
  } catch (error) {
    console.error("URLコピーに失敗しました:", error);
  }
};

// 配信を見る
const viewStreams = () => {
  if (!nowstreaming.value) {
    return;
  }
  router.push(`/crowdfunding/${campaignId.value}/live`);
};

// 決済ページに遷移
const goToPayment = (returnItem) => {
  if (!returnItem || !returnItem.id) {
    console.error("リターンアイテム情報が不正です");
    return;
  }

  router.push({
    path: `/crowdfunding/${campaignId.value}/payment`,
    query: {
      returnId: returnItem.id,
    },
  });
};

// テスト用ホワイトリスト 将来はFirestoreで管理することも検討
const whitelistedUids = ["asvF5jxezSOc7AmxVCbrzoEXJQ52"];

// 購入者チェック
const checkPurchaser = async (uid) => {
  if (whitelistedUids.includes(uid)) return true;
  try {
    const ordersCollection = collection(db, "users", uid, "Crowdfunding_orders");
    const querySnapshot = await getDocs(ordersCollection);
    for (const orderDoc of querySnapshot.docs) {
      const data = orderDoc.data();
      if (data.orders) {
        const orderSnap = await getDoc(data.orders);
        if (orderSnap.exists() && orderSnap.data().event_id === campaignId.value) {
          return true;
        }
      }
    }
    return false;
  } catch (error) {
    console.error("購入者チェックエラー:", error);
    return false;
  }
};

// 認証状態の監視
let unsubscribeAuth = null;
const setupAuthListener = () => {
  const auth = getAuth();
  unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
    currentUser.value = user;
    if (user) {
      isPurchaser.value = await checkPurchaser(user.uid);
    } else {
      isPurchaser.value = false;
    }
  });
};

onMounted(async () => {
  try {
    await fetchCampaignData();
    await fetchContentBlocks();
    await fetchQAItems();
    await fetchReturnItems();
    await fetchActivityReports();

    // 認証状態の監視を開始
    setupAuthListener();

    // リアルタイムリスナーを設定
    setupCampaignRealtimeListener();
    setupSupportCommentsRealtimeListener();

    window.addEventListener("resize", updateWindowWidth);
    updateWindowWidth();

    // データ読み込み完了後にプレレンダリングイベントを発火
    await nextTick();
    // データ取得が完了したことを確認してからイベント発火
    setTimeout(() => {
      window.dispatchEvent(new Event("render-event"));
    }, 2000); // 2秒待機してからイベント発火（データ取得完了を確実に待つ）
  } catch (error) {
    console.error("データ取得エラー:", error);
    // エラーが発生してもプレレンダリングを続行
    await nextTick();
    setTimeout(() => {
      window.dispatchEvent(new Event("render-event"));
    }, 2000);
  }
});

onUnmounted(() => {
  // 認証リスナーをクリーンアップ
  if (unsubscribeAuth) {
    unsubscribeAuth();
  }
  // 全てのリアルタイムリスナーをクリーンアップ
  unsubscribeFunctions.value.forEach((unsubscribe) => {
    if (unsubscribe) {
      unsubscribe();
    }
  });
  unsubscribeFunctions.value = [];

  window.removeEventListener("resize", updateWindowWidth);
});
</script>

<style scoped>
.crowdfunding-page {
  min-height: 100vh;
  /* padding: 2rem;
  background: #f5f0ff; */
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  border-radius: 12px;
  /* padding: 2rem; */
}

/* メニューセクションより上（メニューセクションは含まない） */
.top-section-wrapper {
  padding: 2rem;
  border-radius: 12px;
  margin-bottom: 2rem;
}

.top-section-wrapper .container {
  padding: 0;
}

/* メインコンテンツ */
.main-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

/* メディアセクション */
.media-section {
  width: 100%;
}

.media-placeholder {
  width: 100%;
  aspect-ratio: 860 / 460;
  background: #e0e0e0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.media-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.placeholder-content p {
  color: #999;
  font-size: 1.2rem;
}

/* 情報セクション */
.info-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.profile-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.profile-image {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
}

.profile-image-placeholder {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-image-placeholder p {
  color: #999;
  font-size: 0.8rem;
}

.vendor-name {
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0;
}

/* タグ */
.tags-section {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.tag {
  background: white;
  color: #4378d3;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  border: 2px solid;
}

/* プロジェクトタイトル */
.project-title {
  font-size: 20px;
  font-weight: bold;
  margin: 0.5rem 0;
  line-height: 1.4;
  text-align: left;
}

/* シェアボタン */
.share-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.share-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 30px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.3s;
}

.share-btn:hover {
  opacity: 0.8;
}

.share-x {
  background: #000;
  color: white;
}

.share-line {
  background: #06c755;
  color: white;
}

.share-url {
  background: #999;
  color: white;
}

.share-icon {
  font-weight: bold;
}

.share-icon-image {
  width: 1rem;
  height: 1rem;
  object-fit: contain;
}

/* 下部セクション */
.bottom-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

/* 進捗セクション */
.progress-section {
  background: #fff;
  padding: 1.5rem;
  border-radius: 10px;
  box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.1);
}

.progress-title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 1.5rem;
}

.amount-display {
  font-size: 40px;
  font-weight: bold;
  margin-bottom: 1rem;
  color: #333;
}

.currency {
  font-size: 1.2rem;
  margin-left: 0.3rem;
}

.progress-bar-container {
  width: 100%;
  height: 26px;
  background: #e0e0e0;
  border-radius: 13px;
  overflow: hidden;
  margin-bottom: 1rem;
  position: relative;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #ffdfea 0%, #ff299d 65%, #b000c7 100%);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 10px;
  transition: width 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: progress-shimmer 3s infinite;
}

.progress-bar::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: shine 3s infinite;
}

.progress-text {
  color: white;
  font-weight: bold;
  font-size: 0.9rem;
}

.metrics {
  display: flex;
  flex-direction: row;
  gap: 0;
  justify-content: space-around;
}

.metric-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.metric-icon {
  font-size: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.metric-icon-image {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.metric-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.metric-label {
  font-size: 16px;
  color: #999;
  margin-bottom: 0.5rem;
}

.metric-value {
  font-size: 20px;
  font-weight: bold;
  color: #333;
}

/* スケジュールセクション */
.schedule-section {
  background: #fff;
  padding: 1.5rem;
  border-radius: 10px;
  box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.1);
}

.schedule-title {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.schedule-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
  margin-right: 0.5rem;
}

.next-stream {
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.next-stream-label {
  font-size: 0.9rem;
  margin: 0;
  color: #333;
  flex: 1;
}

.view-stream-btn {
  color: white;
  border: 2px solid;
  padding: 0.5rem 2.5rem;
  border-radius: 30px;
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  position: relative;
  overflow: hidden;
  animation: shimmer 3s infinite;
}

.view-stream-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: shine 3s infinite;
}

.view-stream-btn:hover {
  opacity: 0.8;
}

.view-stream-btn-disabled {
  cursor: not-allowed;
  opacity: 0.6;
  animation: none;
}

.view-stream-btn-disabled::before {
  display: none;
}

.view-stream-btn-disabled:hover {
  opacity: 0.6;
}

.arrow {
  font-size: 0.8rem;
}

.stream-list {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e0e0e0;
  margin: 0 auto;
}

.stream-item {
  font-size: 0.9rem;
  border-bottom: 1px solid #e0e0e0;
}

.stream-item:last-child {
  border-bottom: none;
}

.stream-label {
  font-weight: 500;
  padding: 0.5rem 1rem;
  text-align: left;
  color: white;
  border-right: 1px solid #e0e0e0;
  width: 30%;
}

.stream-date {
  padding: 0.5rem 0 0.5rem 1rem;
  text-align: left;
  background: white;
  color: #333;
}

.stream-date-content {
  display: block;
}

.stream-archive-mobile {
  display: none;
  margin-top: 0.5rem;
}

.stream-archive {
  padding: 0.5rem;
  text-align: center;
  background: white;
  display: table-cell;
}

.view-archive-btn {
  color: white;
  border: 2px solid;
  padding: 0.4rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: opacity 0.3s;
}

.view-archive-btn:hover {
  opacity: 0.8;
}

/* メッセージセクション */
.message-section {
  margin: 2rem 0;
}

.message-content {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.message-profile-image {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.message-profile-image-placeholder {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.message-profile-image-placeholder p {
  color: #999;
  font-size: 0.7rem;
}

.message-bubble {
  padding: 1.5rem;
  border-radius: 20px;
  position: relative;
  flex: 1;
}

.message-bubble::before {
  content: "";
  position: absolute;
  left: -10px;
  top: 20px;
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-right: 10px solid var(--bubble-arrow-color, #ffb3d9);
}

.message-bubble p {
  margin: 0;
  line-height: 1.6;
  color: #333;
  text-align: left;
}

/* 期間開始前メッセージセクション */
.pre-event-message-section {
  margin-top: 2rem;
  padding: 1.5rem;
  background: #fff;
  border: 3px solid #ff0000;
  border-radius: 0px;
}

.pre-event-message {
  font-size: 1rem;
  margin: 0;
  color: #333;
  text-align: center;
  line-height: 1.6;
}

.pre-event-link {
  text-decoration: underline;
  cursor: pointer;
}

.pre-event-link:hover {
  opacity: 0.7;
}

/* 支援詳細セクション */
.support-details-section {
  margin-top: 2rem;
  padding: 1.5rem;
  background: #fff;
  border: 3px solid;
  border-radius: 0px;
  border-color: var(--support-border-color, #ff6b9d);
}

.support-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 1rem;
  text-align: center;
}

.support-description {
  font-size: 1rem;
  margin-bottom: 1rem;
  color: #333;
  text-align: center;
  line-height: 1.5;
}

.support-conditions {
  display: flex;
  flex-direction: row;
  gap: 1rem;
  justify-content: center;
}

.condition-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.check-icon {
  font-weight: bold;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.condition-item p {
  margin: 0;
  color: #333;
  line-height: 1.5;
  text-align: left;
  font-size: 14px;
}

/* メニューバーセクション */
.menu-section {
  margin-top: 2rem;
}

.menu-container {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 1rem;
}

.menu-title {
  text-align: center;
  font-size: 1rem;
  margin-bottom: 1rem;
  font-weight: normal;
}

.menu-nav {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.menu-item {
  text-decoration: underline;
  font-size: 0.9rem;
  display: inline-block;
  cursor: pointer;
  transition: opacity 0.3s;
}

.menu-text {
  text-decoration: underline;
  text-align: left;
}

.menu-arrow {
  text-decoration: underline;
}

.menu-item:hover {
  opacity: 0.7;
}

.menu-arrow {
  font-size: 0.7rem;
}

/* メニューセクションより下の白背景エリア */
.content-section-white {
  background: white;
  /* padding: 2rem; */
  border-radius: 8px;
  margin-top: 2rem;
}

/* 2カラムセクション */
.two-column-section {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

/* 960px未満では右カラムを非表示 */
.returns-column {
  display: none;
}

@media (min-width: 960px) {
  .two-column-section {
    grid-template-columns: 2fr 1fr;
    align-items: start;
    margin-top: 2rem;
  }

  .project-content-column {
    overflow-y: visible;
  }

  .returns-column {
    display: block;
    position: sticky;
    top: 0;
    align-self: start;
    max-height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
    padding-top: 3rem;
  }

  .profile-links {
    align-items: center;
  }

  .profile-link {
    width: auto;
    justify-content: center;
  }
}

.column-header {
  margin-bottom: 2.5rem;
  text-align: center;
}

.column-label {
  display: block;
  font-size: 20px;
  margin-bottom: 0.5rem;
}

.column-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  position: relative;
  display: inline-block;
}

.column-title-underline {
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 160px;
  height: 1px;
}

.column-body {
  display: flex;
  flex-direction: column;
}

/* プロジェクト内容セクション */
.content-image-placeholder {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #e0e0e0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.content-image-placeholder p {
  color: #999;
  font-size: 1rem;
}

.content-heading-h3 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-top: 15px;
  margin-bottom: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #8e24aa;
  text-align: left;
}

.heading-bullet {
  font-size: 0.8rem;
}

.content-heading-h4 {
  font-size: 20px;
  font-weight: 600;
  margin-top: 15px;
  margin-bottom: 0;
  color: #333;
  text-align: left;
}

.content-image-block {
  width: 100%;
  margin-bottom: 0;
}

.content-image-block img {
  width: 100%;
  height: auto;
  border-radius: 8px;
  margin-top: 1rem;
}

.content-text-block {
  margin-top: 15px;
  margin-bottom: 0;
  text-align: left;
}

.content-text-block p {
  margin: 0;
  line-height: 1.6;
  color: #333;
  text-align: left;
  font-size: 15px;
}

/* リターンカード */
.return-card {
  background: white;
  /* border: 1px solid #e0e0e0; */
  border-radius: 10px;
  padding: 0.5rem;
  margin: 0.6rem;
  margin-bottom: 1rem;
  box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.1);
}

.return-card-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.return-main-section {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
}

.return-price-plan {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  margin-top: 15px;
}

.return-details-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.return-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.return-image,
.return-image-placeholder {
  width: 155px;
  height: 90px;
  border-radius: 4px;
  flex-shrink: 0;
}

.return-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.return-image-placeholder {
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.return-image-placeholder p {
  color: #999;
  font-size: 0.8rem;
}

.return-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
  justify-content: flex-start;
}

.return-price {
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
  text-align: left;
}

.return-plan-name {
  font-size: 14px;
  color: #333;
  text-align: left;
}

.return-details {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
}

.return-detail-item {
  display: flex;
  align-items: center;
  /* gap: 0.5rem; */
  font-size: 0.85rem;
  color: #666;
}

.detail-icon {
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
}

.detail-icon-image {
  /* width: 1rem; */
  height: 15px;
  object-fit: contain;
  margin-right: 0.25rem;
}

.view-details-link {
  font-size: 0.9rem;
  text-decoration: none;
  margin: 0.5rem 0;
  display: inline-block;
}

.support-button {
  color: white;
  border: 2px solid;
  padding: 0.7rem 1.5rem;
  border-radius: 30px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.5rem;
  transition: opacity 0.3s;
  position: relative;
  overflow: hidden;
  animation: shimmer 3s infinite;
}

.support-button::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: shine 3s infinite;
}

.support-button:hover {
  opacity: 0.8;
}

.support-button-disabled {
  cursor: not-allowed;
  opacity: 0.6;
  animation: none;
}

.support-button-disabled::before {
  display: none;
}

.support-button-disabled:hover {
  opacity: 0.6;
}

.return-description {
  margin-top: 1rem;
  text-align: left;
  width: 100%;
}

.return-description p {
  margin: 0;
  color: #333;
  line-height: 1.6;
  font-size: 0.9rem;
  text-align: left;
}

/* Q&Aセクション */
.qa-section {
  margin-top: 2rem;
  padding: 0;
}

.qa-header {
  text-align: center;
  margin-bottom: 2rem;
}

.qa-label {
  display: block;
  font-size: 20px;
  margin-bottom: 0.5rem;
}

.qa-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  position: relative;
  display: inline-block;
}

.qa-content {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.qa-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.qa-question {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  align-items: flex-start;
}

.qa-q-label {
  font-weight: 600;
  flex-shrink: 0;
}

.qa-q-text {
  color: #333;
  line-height: 1.6;
  flex: 1;
  min-width: 0;
  text-align: left;
}

.qa-answer {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  align-items: flex-start;
}

.qa-a-label {
  font-weight: 600;
  flex-shrink: 0;
}

.qa-a-text {
  color: #333;
  line-height: 1.6;
  flex: 1;
  min-width: 0;
  text-align: left;
}

.qa-divider {
  height: 1px;
  background: #e0e0e0;
  margin: 1rem 0;
}

.qa-footer {
  text-align: center;
  margin-top: 1.5rem;
}

.qa-view-more {
  text-decoration: underline;
  font-size: 0.9rem;
  cursor: pointer;
}

.qa-view-more:hover {
  opacity: 0.7;
}

/* 注目のリターンセクション */
.featured-returns-section {
  margin-top: 3rem;
}

.featured-returns-header {
  text-align: center;
  margin-top: 2.5rem;
  margin-bottom: 2rem;
}

.featured-label {
  display: block;
  font-size: 20px;
  margin-bottom: 0.5rem;
}

.featured-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  position: relative;
  display: inline-block;
}

.featured-returns-content {
  display: flex;
  flex-direction: column;
}

.featured-return-card {
  background: white;
  border-radius: 10px;
  padding: 1.5rem;
  margin: 2rem;
  box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.1);
}

.featured-return-main {
  display: flex;
  gap: 1rem;
}

.featured-return-image,
.featured-return-image-placeholder {
  width: 320px;
  height: 180px;
  flex-shrink: 0;
  border-radius: 8px;
}

.featured-return-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.featured-return-image-placeholder {
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.featured-return-image-placeholder p {
  color: #999;
  font-size: 0.9rem;
}

.featured-return-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.featured-return-price {
  margin-top: 0.5rem;
  font-size: 24px;
  font-weight: bold;
  color: #333;
  text-align: left;
}

.featured-return-plan-name {
  font-size: 18px;
  color: #333;
  font-weight: 500;
  text-align: left;
  line-height: 1.3;
}

.featured-return-details {
  display: flex;
  flex-direction: row;
  gap: 1rem;
  margin: 0.5rem 0;
}

.featured-detail-item {
  font-size: 14px;
}

.featured-support-button {
  color: white;
  border: 2px solid;
  padding: 0.7rem 1.5rem;
  border-radius: 30px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.5rem;
  transition: opacity 0.3s;
  width: fit-content;
  position: relative;
  overflow: hidden;
  animation: shimmer 3s infinite;
}

.featured-support-button::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: shine 3s infinite;
}

.featured-support-button:hover {
  opacity: 0.8;
}

.featured-return-description {
  margin-top: 1rem;
  text-align: left;
}

.featured-return-description p {
  margin: 0;
  color: #333;
  line-height: 1.6;
  font-size: 0.9rem;
  text-align: left;
}

.featured-return-description p.description-collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.featured-view-details {
  text-align: center;
  text-decoration: underline;
  font-size: 0.9rem;
  margin-top: 1rem;
  display: block;
}

.featured-view-details:hover {
  opacity: 0.7;
}

/* 活動報告セクション */
.activity-report-section {
  margin-top: 3rem;
}

.activity-report-header {
  text-align: center;
  margin-bottom: 2rem;
}

.activity-label {
  display: block;
  font-size: 20px;
  margin-bottom: 0.5rem;
}

.activity-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  position: relative;
  display: inline-block;
}

.activity-report-content {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.activity-report-locked {
  text-align: center;
  padding: 2.5rem 1.5rem;
  background-color: #f8f8f8;
  border-radius: 12px;
  border: 1px dashed #ddd;
}

.activity-report-locked-message {
  margin: 0 0 1rem;
  font-size: 0.95rem;
  color: #666;
}

.activity-report-login-button {
  display: inline-block;
  padding: 0.7rem 2rem;
  color: #fff;
  border: none;
  border-radius: 25px;
  font-size: 0.95rem;
  cursor: pointer;
  transition: opacity 0.2s;
}

.activity-report-login-button:hover {
  opacity: 0.8;
}

.activity-report-empty {
  text-align: center;
  padding: 2rem;
  color: #999;
}

.activity-report-empty p {
  margin: 0;
  font-size: 1rem;
}

.activity-report-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-align: left;
}

.activity-report-item-title {
  font-size: 1.2rem;
  font-weight: bold;
  margin: 0;
  color: #333;
}

.activity-report-item-date {
  font-size: 0.9rem;
  color: #999;
  margin: 0;
}

.activity-report-item-body {
  margin-top: 0.5rem;
  margin-bottom: 1rem;
}

.activity-content-image-block {
  width: 100%;
  margin-bottom: 1rem;
}

.activity-content-image-block img {
  width: 100%;
  height: auto;
  border-radius: 8px;
}

.activity-content-heading-h3 {
  font-size: 1rem;
  font-weight: 600;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  color: #333;
  text-align: left;
}

.activity-content-heading-h4 {
  font-size: 0.95rem;
  font-weight: 600;
  margin-top: 0.8rem;
  margin-bottom: 0.5rem;
  color: #333;
  text-align: left;
}

.activity-content-text-block {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  text-align: left;
}

.activity-content-text-block p {
  margin: 0;
  line-height: 1.6;
  color: #333;
  text-align: left;
  font-size: 16px;
}

.activity-report-divider {
  height: 1px;
  background: #e0e0e0;
  margin: 1.5rem 0;
}

.activity-report-footer {
  text-align: center;
  margin-top: 1.5rem;
}

.activity-report-view-more {
  text-decoration: underline;
  font-size: 0.9rem;
  cursor: pointer;
}

.activity-report-view-more:hover {
  opacity: 0.7;
}

/* 応援コメントセクション */
.support-comments-section {
  margin-top: 3rem;
}

.support-comments-header {
  text-align: center;
  margin-bottom: 2rem;
}

.support-comments-label {
  display: block;
  font-size: 20px;
  margin-bottom: 0.5rem;
}

.support-comments-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  position: relative;
  display: inline-block;
}

.support-comments-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.support-comments-empty {
  text-align: center;
  padding: 2rem;
  color: #999;
}

.support-comments-empty p {
  margin: 0;
  font-size: 1rem;
}

.support-comment-item {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.support-comment-avatar {
  flex-shrink: 0;
}

.comment-avatar-image {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
}

.comment-avatar-placeholder {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #e0e0e0;
}

.support-comment-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.support-comment-nickname {
  font-size: 1rem;
  font-weight: 600;
  text-align: left;
}

.support-comment-message {
  font-size: 0.95rem;
  color: #333;
  line-height: 1.6;
  word-wrap: break-word;
  text-align: left;
}

.support-comments-footer {
  text-align: center;
  margin-top: 1.5rem;
}

.view-all-comments-btn {
  color: white;
  border: 2px solid;
  padding: 0.7rem 1.5rem;
  border-radius: 30px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  transition: opacity 0.3s;
}

.view-all-comments-btn:hover {
  opacity: 0.8;
}

/* 全てのリターンセクション（左カラム） */
.all-returns-section-left {
  padding-top: 3rem;
}

.return-card-left {
  background: white;
  /* border: 1px solid #e0e0e0; */
  border-radius: 8px;
  padding: 1rem;
  margin: 2rem;
  margin-bottom: 1rem;
  box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.1);
}

.return-card-content-left {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.return-image-section-left {
  width: 100%;
}

.return-image-left,
.return-image-placeholder-left {
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 4px;
  flex-shrink: 0;
}

.return-image-left img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.return-image-placeholder-left {
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.return-image-placeholder-left p {
  color: #999;
  font-size: 0.8rem;
}

.return-info-section-left {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.return-price-plan-left {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.return-price-left {
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
  text-align: left;
}

.return-plan-name-left {
  font-size: 14px;
  color: #333;
  text-align: left;
}

.return-details-left {
  display: flex;
  flex-direction: row;
  gap: 1rem;
  flex-wrap: wrap;
}

.return-detail-item-left {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #666;
}

.return-actions-left {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.view-details-link-left {
  font-size: 0.9rem;
  text-decoration: underline;
  margin: 0;
  display: inline-block;
}

.support-button-left {
  color: white;
  border: 2px solid;
  padding: 0.7rem 1.5rem;
  border-radius: 30px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  transition: opacity 0.3s;
  position: relative;
  overflow: hidden;
  animation: shimmer 3s infinite;
}

.support-button-left::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: shine 3s infinite;
}

.support-button-left:hover {
  opacity: 0.8;
}

.return-description-left {
  margin-top: 1rem;
  text-align: left;
  width: 100%;
}

.return-description-left p {
  margin: 0;
  color: #333;
  line-height: 1.6;
  font-size: 0.9rem;
  text-align: left;
}

/* プロフィールセクション */
.profile-section-left {
  padding: 2rem;
  margin-top: 2rem;
  margin-bottom: 2rem;
  border-radius: 12px;
}

.profile-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.profile-card-image {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
}

.profile-card-image-placeholder {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-card-image-placeholder p {
  color: #999;
  font-size: 0.9rem;
}

.profile-card-name {
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0;
  color: #333;
  text-align: center;
}

.profile-links {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  align-items: flex-start;
}

.profile-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: #333;
  width: 100%;
}

.profile-link-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #ffb3d9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.profile-link-icon-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 50%;
}

.profile-link-text {
  text-decoration: underline;
  font-size: 0.9rem;
  word-break: break-all;
}

/* 詳細展開アニメーション */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.slide-down-enter-from {
  max-height: 0;
  opacity: 0;
  margin-top: 0;
}

.slide-down-enter-to {
  max-height: 500px;
  opacity: 1;
  margin-top: 1rem;
}

.slide-down-leave-from {
  max-height: 500px;
  opacity: 1;
  margin-top: 1rem;
}

.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
  margin-top: 0;
}

@media (min-width: 768px) {
  .return-card-content-left {
    flex-direction: row;
    align-items: flex-start;
    gap: 4rem;
  }

  .return-image-section-left {
    width: 200px;
    flex-shrink: 0;
  }

  .return-info-section-left {
    flex: 1;
  }
}

/* レスポンシブ対応 */
@media (max-width: 768px) {
  .main-content {
    grid-template-columns: 1fr;
  }

  .bottom-section {
    grid-template-columns: 1fr;
  }
  media-section .container {
    padding: 1rem;
  }

  .share-buttons {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .share-btn {
    width: auto;
    flex: 1;
    min-width: 0;
  }

  .schedule-title {
    flex-direction: column;
    gap: 0.5rem;
    font-size: 1.2rem;
  }

  .schedule-icon {
    margin-right: 0;
    width: 60px;
    height: 60px;
  }

  .next-stream {
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 2rem;
  }

  .view-stream-btn {
    /* width: 40%; */
    padding: 1rem 2.5rem;
    justify-content: center;
    font-size: 1.2rem;
  }

  .stream-list {
    width: auto;
    max-width: 90%;
  }

  .message-content {
    flex-direction: column;
    align-items: center;
  }

  .message-bubble {
    width: 90%;
    flex: none;
  }

  .message-bubble::before {
    display: none;
  }

  .support-conditions {
    flex-direction: column;
    align-items: center;
  }

  .content-section-white {
    padding: 1rem;
    margin-left: -1rem;
    margin-right: -1rem;
    border-radius: 0;
  }

  .project-content-column {
    overflow-x: hidden;
    max-width: 100%;
    width: 100%;
    margin-top: 2rem;
  }

  .content-image-block img,
  .activity-content-image-block img {
    max-width: 100%;
    height: auto;
  }

  .featured-return-card {
    /* margin: 1rem 0; */
    padding: 1rem;
  }

  .featured-return-main {
    flex-direction: column;
    gap: 1rem;
  }

  .featured-return-image,
  .featured-return-image-placeholder {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
  }

  .featured-return-info {
    align-items: flex-start;
    text-align: left;
    gap: 0.5rem;
  }

  .featured-return-price {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    text-align: left;
  }

  .featured-return-plan-name {
    text-align: left;
  }

  .featured-return-details {
    justify-content: center;
    flex-wrap: wrap;
  }

  .featured-support-button {
    width: 100%;
    justify-content: center;
  }

  .featured-return-description {
    text-align: center;
  }

  .featured-return-description p {
    text-align: center;
  }
  .qa-section {
    padding: 1rem;
  }
  .column-body-content {
    padding: 1rem;
  }
  .activity-report-section {
    padding: 1rem;
  }
  .support-comments-section {
    padding: 1rem;
  }

  .return-card-left {
    margin: 1rem;
  }

  .return-card-content-left {
    display: grid;
    grid-template-columns: 155px 1fr;
    grid-template-rows: auto auto auto;
    gap: 1rem;
    align-items: start;
  }

  .return-image-section-left {
    grid-column: 1;
    grid-row: 1;
    width: 155px;
    flex-shrink: 0;
  }

  .return-info-section-left {
    grid-column: 2;
    grid-row: 1;
    display: contents;
  }

  .return-price-plan-left {
    grid-column: 2;
    grid-row: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
    text-align: left;
    margin-top: 0.5rem;
  }

  .return-details-left {
    grid-column: 1 / -1;
    grid-row: 2;
    display: flex;
    flex-direction: row;
    gap: 1rem;
    flex-wrap: wrap;
    width: 100%;
    justify-content: flex-start;
  }

  .return-actions-left {
    grid-column: 1 / -1;
    grid-row: 3;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    width: 100%;
  }

  .return-price-left {
    text-align: left;
  }

  .return-plan-name-left {
    text-align: left;
  }

  .view-details-link-left {
    flex: 1;
    text-align: center;
  }

  .support-button-left {
    flex: 1;
    justify-content: center;
  }
  .next-stream-label {
    margin-bottom: 1rem;
  }
  .stream-label {
    font-size: 0.6rem;
  }
  .stream-date {
    font-size: 0.8rem;
  }

  .stream-archive {
    display: none;
  }

  .stream-archive-mobile {
    display: block;
    margin-top: 0.5rem;
    text-align: center;
  }

  .view-archive-btn {
    /* width: 100%; */
    max-width: 200px;
    margin: 0 auto;
    font-size: 0.6rem;
    padding: 0.3rem 1rem;
  }
  .menu-section {
    padding: 1rem;
  }
  .profile-section-left {
    margin-left: 1rem;
    margin-right: 1rem;
  }
}

/* 応援支援するボタンの光るアニメーション */
@keyframes shimmer {
  0%,
  100% {
    box-shadow: 0 0 3px rgba(67, 120, 211, 0.3), 0 0 6px rgba(67, 120, 211, 0.2);
  }
  50% {
    box-shadow: 0 0 8px rgba(67, 120, 211, 0.5), 0 0 15px rgba(67, 120, 211, 0.3),
      0 0 20px rgba(195, 240, 255, 0.2);
  }
}

@keyframes shine {
  0% {
    left: -100%;
  }
  50%,
  100% {
    left: 100%;
  }
}

/* 進捗バーの光るアニメーション */
@keyframes progress-shimmer {
  0%,
  100% {
    box-shadow: 0 0 5px rgba(255, 41, 157, 0.4), 0 0 10px rgba(255, 41, 157, 0.3);
  }
  50% {
    box-shadow: 0 0 15px rgba(255, 41, 157, 0.6), 0 0 25px rgba(255, 41, 157, 0.4),
      0 0 35px rgba(176, 0, 199, 0.3);
  }
}
</style>
